﻿import { useEffect } from 'react';

export interface SEOConfig {
  title: string;
  description: string;
  canonical: string;
  ogImage: string;
  ogType?: string;
  noindex?: boolean;
  faq?: { question: string; answer: string }[];
  serviceType?: string;
  areaServed?: string;
}

function setMeta(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeJsonLd() {
  const existing = document.querySelectorAll('script[data-seo-jsonld]');
  existing.forEach((s) => s.remove());
}

function injectJsonLd(data: object | object[]) {
  removeJsonLd();
  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-seo-jsonld', 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function buildLocalBusiness(areaServed?: string) {
  return {
    '@type': 'HomeAndConstructionBusiness',
    '@id': 'https://reparation-fenetre-strasbourg.fr/#business',
    name: 'DF PRO Réparation Fenêtre',
    telephone: '+33374471036',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '82 rue de Heidenberg',
      addressLocality: 'Strasbourg',
      postalCode: '67200',
      addressCountry: 'FR',
    },
    areaServed: areaServed
      ? { '@type': 'City', name: areaServed }
      : { '@type': 'AdministrativeArea', name: 'Bas-Rhin' },
    url: 'https://reparation-fenetre-strasbourg.fr',
    priceRange: '€€',
  };
}

function buildService(serviceType: string, areaServed?: string) {
  return {
    '@type': 'Service',
    serviceType,
    provider: { '@id': 'https://reparation-fenetre-strasbourg.fr/#business' },
    areaServed: areaServed
      ? { '@type': 'City', name: areaServed }
      : { '@type': 'AdministrativeArea', name: 'Bas-Rhin' },
  };
}

function buildFAQPage(faqs: { question: string; answer: string }[]) {
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function useSEO(config: SEOConfig) {
  useEffect(() => {
    document.title = config.title;
    setMeta('description', config.description);
    setMeta('robots', config.noindex ? 'noindex, nofollow' : 'index, follow');
    setLink('canonical', config.canonical);

    setMeta('og:title', config.title, true);
    setMeta('og:description', config.description, true);
    setMeta('og:type', config.ogType || 'website', true);
    setMeta('og:url', config.canonical, true);
    setMeta('og:image', config.ogImage, true);
    setMeta('og:locale', 'fr_FR', true);
    setMeta('og:site_name', 'DF PRO Réparation Fenêtre', true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', config.title);
    setMeta('twitter:description', config.description);
    setMeta('twitter:image', config.ogImage);

    const graph: object[] = [buildLocalBusiness(config.areaServed)];
    if (config.serviceType) {
      graph.push(buildService(config.serviceType, config.areaServed));
    }
    if (config.faq && config.faq.length > 0) {
      graph.push(buildFAQPage(config.faq));
    }

    injectJsonLd({
      '@context': 'https://schema.org',
      '@graph': graph,
    });

    return () => {
      removeJsonLd();
    };
  }, [config]);
}

